export interface PartnerInviteRecord {
  id: string;
  grantingTenantId: string;
  createdByUserId?: string | null;
  grantType: string;
  status: 'active' | 'expired' | 'disabled' | 'consumed' | string;
  inviteToken: string;
  approvalRequired: boolean;
  expiresAt?: string | null;
  maxUses?: number | null;
  useCount: number;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface PartnerGrantRecord {
  id: string;
  grantingTenantId: string;
  receivingTenantId: string;
  inviteId?: string | null;
  grantType: string;
  status: 'requested' | 'approved' | 'rejected' | 'revoked' | string;
  requestedByUserId: string;
  reviewedByUserId?: string | null;
  approvedAt?: string | null;
  reviewedAt?: string | null;
  expiresAt?: string | null;
  metadata?: Record<string, unknown> | null;
  requestedAt: string;
  createdAt: string;
  updatedAt?: string | null;
  grantingTenantName?: string;
  grantingTenantSlug?: string;
  receivingTenantName?: string;
  receivingTenantSlug?: string;
}

export interface PartnerInviteRedeemResult {
  grant: PartnerGrantRecord;
  createdRequest: boolean;
  approved: boolean;
  alreadyApproved: boolean;
}

interface InviteListResponse {
  items?: PartnerInviteRecord[];
}

interface GrantListResponse {
  items?: PartnerGrantRecord[];
  total_count?: number | null;
  limit?: number | null;
  offset?: number | null;
  facets?: Record<string, PartnerFacetBucket[] | null> | null;
}

export interface PartnerFacetBucket {
  value: string;
  label?: string | null;
  count: number;
}

export interface PartnerRegistryPage {
  items: PartnerGrantRecord[];
  total_count: number;
  limit: number;
  offset: number;
  facets: Record<string, PartnerFacetBucket[]>;
}

export interface PartnerRegistryListInput {
  search?: string;
  limit?: number;
  offset?: number;
  sort?: string[];
  include_facets?: string[];
}

export interface PartnerInviteCreatePayload {
  grantType?: string;
  approvalRequired?: boolean;
  expiresAt?: string | null;
  maxUses?: number | null;
  metadata?: Record<string, unknown> | null;
}

export class PartnerLinkError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'PartnerLinkError';
    this.status = status;
  }
}

async function readJSON<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const body = await response.json() as { message?: string; detail?: string; title?: string };
      message = body.message || body.detail || body.title || message;
    } catch {
      // ignore body parsing failures
    }
    throw new PartnerLinkError(message, response.status);
  }

  return readJSON<T>(response);
}

function buildStatusQuery(statuses?: string[]): string {
  if (!statuses?.length) {
    return '';
  }
  const query = new URLSearchParams();
  statuses.filter(Boolean).forEach(status => query.append('status', status));
  const encoded = query.toString();
  return encoded ? `?${encoded}` : '';
}

function buildRegistryQuery(filters: PartnerRegistryListInput): string {
  const query = new URLSearchParams();
  if (filters.search?.trim()) query.set('search', filters.search.trim().slice(0, 200));
  if (filters.limit != null) query.set('limit', String(filters.limit));
  if (filters.offset != null) query.set('offset', String(filters.offset));
  filters.sort?.filter(Boolean).forEach(value => query.append('sort', value));
  filters.include_facets?.filter(Boolean).forEach(value => query.append('include_facets', value));
  const encoded = query.toString();
  return encoded ? `?${encoded}` : '';
}

function normalizeRegistryPage(payload: GrantListResponse, filters: PartnerRegistryListInput): PartnerRegistryPage {
  const items = payload.items ?? [];
  return {
    items,
    total_count: payload.total_count ?? items.length,
    limit: payload.limit ?? filters.limit ?? items.length,
    offset: payload.offset ?? filters.offset ?? 0,
    facets: Object.fromEntries(Object.entries(payload.facets ?? {}).map(([key, buckets]) => [key, buckets ?? []])),
  };
}

export async function listTenantPartnerInvites(tenantId: string): Promise<PartnerInviteRecord[]> {
  const payload = await request<InviteListResponse>(`/api/v1/tenants/${encodeURIComponent(tenantId)}/partner-invites`);
  return payload.items ?? [];
}

export async function createTenantPartnerInvite(tenantId: string, payload: PartnerInviteCreatePayload): Promise<PartnerInviteRecord> {
  return request<PartnerInviteRecord>(`/api/v1/tenants/${encodeURIComponent(tenantId)}/partner-invites`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function listTenantPartnerRequests(tenantId: string, statuses?: string[]): Promise<PartnerGrantRecord[]> {
  const payload = await request<GrantListResponse>(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/partner-invites/requests${buildStatusQuery(statuses)}`,
  );
  return payload.items ?? [];
}

async function updateTenantPartnerRequest(tenantId: string, requestId: string, action: 'approve' | 'reject' | 'revoke') {
  return request<PartnerGrantRecord>(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/partner-invites/requests/${encodeURIComponent(requestId)}/${action}`,
    { method: 'POST' },
  );
}

export function approveTenantPartnerRequest(tenantId: string, requestId: string) {
  return updateTenantPartnerRequest(tenantId, requestId, 'approve');
}

export function rejectTenantPartnerRequest(tenantId: string, requestId: string) {
  return updateTenantPartnerRequest(tenantId, requestId, 'reject');
}

export function revokeTenantPartnerRequest(tenantId: string, requestId: string) {
  return updateTenantPartnerRequest(tenantId, requestId, 'revoke');
}

export async function listLinkedResellers(tenantId: string, filters: PartnerRegistryListInput = {}): Promise<PartnerRegistryPage> {
  const payload = await request<GrantListResponse>(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/linked-resellers${buildRegistryQuery(filters)}`,
  );
  return normalizeRegistryPage(payload, filters);
}

export async function listLinkedSellers(tenantId: string, filters: PartnerRegistryListInput = {}): Promise<PartnerRegistryPage> {
  const payload = await request<GrantListResponse>(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/linked-sellers${buildRegistryQuery(filters)}`,
  );
  return normalizeRegistryPage(payload, filters);
}

export async function redeemPartnerInvite(token: string, receivingTenantId: string): Promise<PartnerInviteRedeemResult> {
  return request<PartnerInviteRedeemResult>('/api/v1/partner-invites/redeem', {
    method: 'POST',
    body: JSON.stringify({
      token,
      receivingTenantId,
    }),
  });
}
